/**
 * @fileoverview Provides utility functions for applying and managing file rewrites and diff changes. Supports editing, creating, and removing files with a flexible rewrite mechanism that can track line ranges and handle strict or overwrite modes of file modification.
 */

import { existsSync, readFileSync, unlinkSync, writeFileSync } from 'fs'
import * as Diff from 'diff'
import { debug_block, error } from '@iterator/shared-node/util/logging'

export type LineRange = {
  // Inclusive.
  start: number
  // Exclusive.
  end: number
}

// Rewrites must be applied in sequence so that line ranges can be tracked accurately.
export type Rewrite = {
  originalLineRange: LineRange
  originalLines: string[]
  newLineRange: LineRange
  newLines: string[]
}

export type RewriteType = 'editFile' | 'createFile' | 'removeFile'

// Fully specify a Rewrite that could be recorded in history.
export type RewriteWithPath = Rewrite & {
  type: RewriteType
  filePath: string
}

/**
 * Applies a series of rewrites to the original lines. Each Rewrite's line numbers assume the previous Rewrites have been applied.
 *
 * If mode = 'strict', will throw an error if the original lines do not match the expected lines.
 */
export function applyEditRewrites(
  lines: string[],
  rewrites: Rewrite[],
  mode: RewriteMode,
): string[] {
  for (const rewrite of rewrites) {
    if (
      mode === 'strict' &&
      lines.slice(rewrite.originalLineRange.start, rewrite.originalLineRange.end).join('\n') !==
        rewrite.originalLines.join('\n')
    ) {
      throw new Error('Applying rewrites failed. Original lines do not match rewrite lines.')
    }
    lines = [
      ...lines.slice(0, rewrite.originalLineRange.start),
      ...rewrite.newLines,
      ...lines.slice(rewrite.originalLineRange.end),
    ]
  }
  return lines
}

/**
 * Create or remove a file.
 *
 * If mode = 'strict', will throw an error if the file already exists or does not exist (depending on the rewrite type) or lines do not match.
 */
function applyCreateOrRemoveRewrite(rewrite: RewriteWithPath, mode: RewriteMode) {
  if (rewrite.type === 'createFile') {
    if (mode === 'strict' && existsSync(rewrite.filePath)) {
      throw new Error(`File ${rewrite.filePath} already exists.`)
    }
    writeFileSync(rewrite.filePath, rewrite.newLines.join('\n'))
  } else {
    if (mode === 'strict') {
      const fileExists = existsSync(rewrite.filePath)
      if (!fileExists) {
        throw new Error(`File ${rewrite.filePath} does not exist.`)
      }
      const lines = readFileSync(rewrite.filePath, 'utf8').split('\n')
      if (lines.join('\n') !== rewrite.originalLines.join('\n')) {
        throw new Error(
          `Applying rewrite failed. Tried to remove file ${rewrite.filePath} but lines do not match.`,
        )
      }
    }
    unlinkSync(rewrite.filePath)
  }
}

export type RewriteMode = 'strict' | 'overwrite'

export function applyRewritesToFiles(rewrites: RewriteWithPath[], mode: RewriteMode = 'overwrite') {
  debug_block('applyRewritesToFiles', rewrites)
  const rewritesByFile = rewrites.reduce(
    (acc, rewrite) => {
      if (!acc[rewrite.filePath]) {
        acc[rewrite.filePath] = []
      }
      acc[rewrite.filePath]!.push(rewrite)
      return acc
    },
    {} as Record<string, RewriteWithPath[]>,
  )

  // Assume if there is a createFile or removeFile for a file, there is only one rewrite.
  for (const [filePath, fileRewrites] of Object.entries(rewritesByFile)) {
    const anyCreateOrRemove = fileRewrites.some(
      (rewrite) => rewrite.type === 'createFile' || rewrite.type === 'removeFile',
    )
    if (anyCreateOrRemove) {
      if (fileRewrites.length !== 1) {
        error(`Failed to apply rewrites to file ${filePath}.`)
        throw new Error('When creating or removing a file, expect that to be the only rewrite.')
      }
      applyCreateOrRemoveRewrite(fileRewrites[0]!, mode)
    } else {
      const lines = readFileSync(filePath, 'utf8').split('\n')
      const newLines = applyEditRewrites(lines, fileRewrites, mode)
      writeFileSync(filePath, newLines.join('\n'))
    }
  }
}

/**
 * Inverts the direction of the rewrites so we can undo a Rewrite.
 */
export function invertRewrite(rewrite: RewriteWithPath): RewriteWithPath {
  const newType =
    rewrite.type === 'editFile'
      ? 'editFile'
      : rewrite.type === 'createFile'
        ? 'removeFile'
        : 'createFile'
  return {
    filePath: rewrite.filePath,
    type: newType,
    originalLines: rewrite.newLines,
    originalLineRange: rewrite.newLineRange,
    newLines: rewrite.originalLines,
    newLineRange: rewrite.originalLineRange,
  }
}

export function rewriteToDiff(rewrite: Rewrite): string {
  return changesToString(
    Diff.diffLines(rewrite.originalLines.join('\n'), rewrite.newLines.join('\n')),
  )
}

export function changesToString(changes: Diff.Change[]) {
  return changes
    .map((change) => {
      if (change.added) {
        return mapLines(change.value, (line) => `+ ${line}`)
      } else if (change.removed) {
        return mapLines(change.value, (line) => `- ${line}`)
      } else {
        return mapLines(change.value, (line) => `  ${line}`)
      }
    })
    .join('\n')
}

export function combineChanges(changes: Diff.Change[]) {
  return changes
    .map((change) => {
      if (change.removed) {
        // Skip removed lines
        return ''
      }
      return change.value
    })
    .join('')
}

// Split on lines and assume string ends in a newline.
function mapLines(lines: string, fn: (line: string) => string) {
  if (lines.endsWith('\n')) {
    return lines.split('\n').slice(0, -1).map(fn).join('\n')
  } else {
    return lines.split('\n').map(fn).join('\n')
  }
}

const isAddOrRemove = (c: Diff.Change | undefined) => c && (c.added || c.removed)

// Given a list of changes, find all that are not 'added' or 'removed'.
// If there is an 'added' or 'removed' before -> include first N lines
// If there is an 'added' or 'removed' after -> include last N lines
// If those don't overlap, include a '\n...\n' between them.
export function truncateDiffChanges(changes: Diff.Change[], N: number): Diff.Change[] {
  // Make a deep copy so we don't mutate the original.
  changes = [...changes.map((c) => ({ ...c }))]

  for (let i = 0; i < changes.length; i++) {
    const change = changes[i]!
    if (!change || isAddOrRemove(change)) {
      continue
    }

    const changeBefore = isAddOrRemove(changes[i - 1])
    const changeAfter = isAddOrRemove(changes[i + 1])

    const endsWithNewline = change.value.endsWith('\n')
    const changeLines = change.value.replace(/\n$/, '').split('\n')
    if (changeBefore && changeAfter) {
      if (changeLines.length > N * 2) {
        change.value =
          [...changeLines.slice(0, N), '\n...\n', ...changeLines.slice(-N)].join('\n') +
          (endsWithNewline ? '\n' : '')
      }
    } else if (changeAfter && changeLines.length > N) {
      change.value = [...changeLines.slice(-N)].join('\n') + (endsWithNewline ? '\n' : '')
    } else if (changeBefore && changeLines.length > N) {
      change.value = [...changeLines.slice(0, N)].join('\n')
    }
  }
  return changes
}

/**
 * Convert a list of diff changes to a list of rewrites. The rewrites are applied in order which means
 * the line numbers assume prior rewrites have been applied.
 */
export function diffChangesToRewrites(changes: Diff.Change[], offset = 0): Rewrite[] {
  if (changes.length === 0) {
    return []
  }
  const firstChange = changes[0]!
  const secondChange = changes[1]

  if (firstChange.added) {
    const addedLines = firstChange.count!
    return [
      {
        originalLineRange: { start: offset, end: offset },
        originalLines: [],
        newLineRange: { start: offset, end: offset + addedLines },
        newLines: firstChange.value.split('\n').slice(0, addedLines),
      },
      ...diffChangesToRewrites(changes.slice(1), offset + addedLines),
    ]
  } else if (firstChange.removed && !secondChange?.added) {
    const removedLines = firstChange.count!
    return [
      {
        originalLineRange: { start: offset, end: offset + removedLines },
        originalLines: firstChange.value.split('\n').slice(0, removedLines),
        newLineRange: { start: offset, end: offset },
        newLines: [],
      },
      ...diffChangesToRewrites(changes.slice(1), offset),
    ]
  } else if (firstChange.removed && secondChange?.added) {
    const removedLines = firstChange.count!
    const addedLines = secondChange.count!
    return [
      {
        originalLineRange: { start: offset, end: offset + removedLines },
        originalLines: firstChange.value.split('\n').slice(0, removedLines),
        newLineRange: { start: offset, end: offset + addedLines },
        newLines: secondChange.value.split('\n').slice(0, addedLines),
      },
      ...diffChangesToRewrites(changes.slice(2), offset + secondChange.count!),
    ]
  }

  return diffChangesToRewrites(changes.slice(1), offset + firstChange.count!)
}
